import { useEffect, useRef } from "react";
import { cn } from "@/shared/lib/utils";
import { ScrollArea } from "@/shared/ui/scroll-area";
import {
  isTimeDisabled, isMinuteDisabled, getFirstAvailableMinute, getFirstAvailableHour,
} from "./utils";

type TimeSelectorProps = {
  selectedDate: Date | undefined
  timeValue: string
  onTimeChange: (time: string) => void
  minuteStep?: number
  minHour?: number
  maxHour?: number
};

export const TimeSelector = ({
  selectedDate, timeValue, onTimeChange, minuteStep = 5, minHour, maxHour,
}: TimeSelectorProps) => {
  const hoursContainerRef = useRef<HTMLDivElement>(null);
  const minutesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToTime = (time: string) => {
    if (!time) return;

    const [selectedHour, selectedMinute] = time.split(":");

    if (selectedHour) {
      const hourButton = hoursContainerRef.current?.querySelector(`[data-hour="${selectedHour}"]`);
      if (hourButton) hourButton.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (selectedMinute && selectedMinute !== "--") {
      const roundedSelectedMinute = Math.round(parseInt(selectedMinute) / minuteStep) * minuteStep;
      const minuteButton = minutesContainerRef.current?.querySelector(`[data-minute="${roundedSelectedMinute}"]`);
      if (minuteButton) minuteButton.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const scrollToFirstAvailableTime = (hour?: string) => {
    if (!selectedDate || timeValue) return;
    const firstAvailableHour = hour ?? getFirstAvailableHour(selectedDate);
    const firstAvailableMinutes = getFirstAvailableMinute(selectedDate, parseInt(firstAvailableHour));
    scrollToTime(`${firstAvailableHour}:${firstAvailableMinutes}`);
  };

  useEffect(() => {
    if (timeValue) {
      scrollToTime(timeValue);
    }
    else {
      scrollToFirstAvailableTime();
    }
  }, [selectedDate, timeValue]);

  const handleHourClick = (hour: string) => {
    if (!selectedDate) return;

    scrollToFirstAvailableTime(hour);
    onTimeChange(`${hour}:--`);
  };

  const handleMinuteClick = (minute: string) => {
    const selectedHour = timeValue ? timeValue.split(":")[0] : "00";
    onTimeChange(`${selectedHour}:${minute}`);
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-sm font-medium mb-2">Heures</div>
        <ScrollArea className="h-32" onWheelCapture={(event) => event.stopPropagation()}>
          <div ref={hoursContainerRef} className="grid grid-cols-1 gap-1">
            {Array.from(
              { length: (maxHour ?? 23) - (minHour ?? 0) + 1 },
              (_, i) => i + (minHour ?? 0),
            ).map((i) => {
              const hour = i.toString().padStart(2, "0");
              const isDisabled = selectedDate ? isTimeDisabled(selectedDate, i) : false;
              const isSelected = timeValue?.split(":")[0] === hour;

              return (
                <button
                  key={hour}
                  type="button"
                  data-hour={hour}
                  onClick={() => handleHourClick(hour)}
                  disabled={isDisabled}
                  className={cn(
                    "h-8 w-full rounded-md text-xs font-normal transition-colors",
                    "hover:bg-accent hover:text-accent-foreground hover:cursor-pointer",
                    isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                    isDisabled && "text-muted-foreground opacity-50 cursor-not-allowed hover:bg-transparent hover:cursor-not-allowed",
                  )}
                >
                  {hour}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      <div>
        <div className="text-sm font-medium mb-2">Minutes</div>
        <ScrollArea className="h-32" onWheelCapture={(event) => event.stopPropagation()}>
          <div ref={minutesContainerRef} className="grid grid-cols-1 gap-1">
            {Array.from({ length: 60 / minuteStep }, (_, i) => {
              const minute = (i * minuteStep).toString().padStart(2, "0");
              const selectedHour = timeValue ? timeValue.split(":")[0] : "00";
              const selectedHourNum = parseInt(selectedHour);
              const minuteValue = i * minuteStep;

              // Disable minutes > 0 if we're at maxHour (e.g., 18h only allows :00)
              const isMaxHourRestriction = maxHour !== undefined && selectedHourNum === maxHour && minuteValue > 0;

              const isDisabled = isMaxHourRestriction || (selectedDate ?
                isMinuteDisabled(selectedDate, selectedHourNum, minuteValue)
                : false);
              const isSelected = timeValue?.split(":")[1] === minute;

              return (
                <button
                  key={minute}
                  type="button"
                  data-minute={minute}
                  onClick={() => handleMinuteClick(minute)}
                  disabled={isDisabled}
                  className={cn(
                    "h-8 w-full rounded-md text-xs font-normal transition-colors",
                    "hover:bg-accent hover:text-accent-foreground hover:cursor-pointer",
                    isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                    isDisabled && "text-muted-foreground opacity-50 cursor-not-allowed hover:bg-transparent hover:cursor-not-allowed",
                  )}
                >
                  {minute}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
